/**
 * Announcements Service
 *
 * Fetches global/plan-scoped announcements shown in the NotificationBell.
 */

import { api } from '../utils/api'
import { reportError } from '../utils/reportError'
import { trackObservability } from '../utils/telemetry'

export type AnnouncementSeverity = 'info' | 'warning' | 'critical'

export type SubscriptionTier =
  | 'starter_free'
  | 'basic'
  | 'professional'
  | 'enterprise'

export interface AnnouncementLinkedSet {
  id: string
  gameId: string
  setName: string
  setCode: string
  setType?: 'set' | 'promo' | 'deck' | 'collection' | 'energy' | 'vending' | 'other'
  releaseDate?: string | null
  printedTotal?: number | null
  actualTotal?: number | null
  language?: string
  symbolUrl?: string | null
  logoUrl?: string | null
  coverImageUrl?: string | null
  gameName?: string | null
}

export interface AnnouncementUserState {
  readAt: string | null
  dismissedAt: string | null
}

export interface Announcement {
  id: string
  title: string
  message: string
  severity: AnnouncementSeverity
  targetTier: SubscriptionTier | null
  gameId?: string | null
  setId?: string | null
  linkedSet?: AnnouncementLinkedSet | null
  startsAt: string
  expiresAt: string
  createdAt: string
  updatedAt: string
  /** Present when the request is authenticated; drives bell read/dismiss (server-backed). */
  userState?: AnnouncementUserState
  /** Admin API only */
  createdByUserId?: string | null
}

export const listAnnouncements = async (opts?: { bypassCache?: boolean }): Promise<Announcement[]> => {
  const headers = opts?.bypassCache
    ? { 'X-Client-No-Dedupe': true as const }
    : {
        'X-Client-Cache': true as const,
        'X-Client-Cache-Ttl': 30000,
      }

  try {
    const response = await api.get('/announcements', { headers })

    return response.data.data.announcements || []
  } catch (err) {
    reportError(err, { context: 'announcementsService.listAnnouncements' })
    trackObservability('announcements_list_failed', {
      message: err instanceof Error ? err.message : String(err),
    })
    return []
  }
}

export interface UpdateAnnouncementStatesInput {
  announcementId?: string
  announcementIds?: string[]
  read?: boolean
  dismissed?: boolean
}

export const updateAnnouncementStates = async (input: UpdateAnnouncementStatesInput): Promise<void> => {
  await api.post('/announcements/me/state', input)
}

export const listAdminAnnouncements = async (opts?: { includeExpired?: boolean }): Promise<Announcement[]> => {
  const includeExpired = opts?.includeExpired ? '1' : '0'
  const response = await api.get(`/announcements/admin?includeExpired=${includeExpired}`)
  return response.data.data.announcements || []
}

export interface CreateAnnouncementInput {
  title: string
  message: string
  severity: AnnouncementSeverity
  targetTier?: SubscriptionTier | null
  ttlMinutes?: number
  expiresAt?: string
  gameId?: string | null
  setId?: string | null
}

export const createAnnouncement = async (input: CreateAnnouncementInput): Promise<Announcement> => {
  const response = await api.post('/announcements', input)
  return response.data.data
}

export const deleteAnnouncement = async (id: string): Promise<void> => {
  await api.delete(`/announcements/${encodeURIComponent(id)}`)
}

export interface UpdateAnnouncementInput {
  title?: string
  message?: string
  severity?: AnnouncementSeverity
  expiresAt?: string
  targetTier?: SubscriptionTier | null
  gameId?: string | null
  setId?: string | null
}

export const updateAnnouncement = async (id: string, input: UpdateAnnouncementInput): Promise<Announcement> => {
  const response = await api.put(`/announcements/${encodeURIComponent(id)}`, input)
  return response.data.data
}

export const announcementsService = {
  listAnnouncements,
  listAdminAnnouncements,
  createAnnouncement,
  deleteAnnouncement,
  updateAnnouncement,
  updateAnnouncementStates,
}
